import { createBigDecimal, toStringBigDecimal } from '@0xtorch/big-decimal'
import { stringify } from '@0xtorch/core'
import type { z } from 'zod'
import type {
  Erc20TokenTransfer,
  Erc721TokenTransfer,
  Erc1155TokenTransfer,
  Log,
  LowerHex,
  TransactionDecoded,
} from '../../../../types'
import { checkValueIsAddressIn } from './addressIn'
import { checkValueIsBetweenBigInt } from './betweenBigInt'
import type { simpleValueSchema } from './schema'

export const getSimpleValue = ({
  schema,
  transaction,
  relatedAddresses,
}: {
  readonly schema: z.infer<typeof simpleValueSchema>
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
}): string => {
  if (typeof schema === 'string') {
    return schema
  }

  switch (schema.type) {
    case 'chain-id': {
      return transaction.chainId.toString()
    }
    case 'tx-hash': {
      return transaction.hash
    }
    case 'from': {
      return transaction.from.address
    }
    case 'to': {
      return transaction.to?.address ?? ''
    }
    case 'value': {
      return transaction.value.toString()
    }
    case 'function': {
      const arg = transaction.function?.args[schema.index].value
      if (schema.paramKeys === undefined) {
        return stringify(arg).toLowerCase()
      }
      let value = arg
      for (const key of schema.paramKeys) {
        if (typeof key === 'number') {
          value = (value as unknown[])[key]
        }
        value = (value as Record<string, unknown>)[key]
      }
      return stringify(value).toLowerCase()
    }
    case 'log': {
      const targetLogs: Log[] = []
      for (const log of transaction.logs) {
        // address
        if (
          schema.address !== undefined &&
          !checkValueIsAddressIn({
            value: log.address,
            schema: schema.address,
            transaction,
            relatedAddresses,
          })
        ) {
          continue
        }

        // signature
        if (
          schema.signature !== undefined &&
          log.topics[0] !== schema.signature
        ) {
          continue
        }

        // indexedCount
        if (
          schema.indexedCount !== undefined &&
          log.topics.length - 1 !== schema.indexedCount
        ) {
          continue
        }

        // args
        if (schema.args !== undefined) {
          if (log.args === undefined) {
            continue
          }

          let unmatched = false
          for (const argSchema of schema.args) {
            const argValue = stringify(
              log.args[argSchema.index].value,
            ).toLowerCase()
            if (
              typeof argSchema.value === 'string' &&
              argSchema.value !== argValue
            ) {
              unmatched = true
              break
            }
            if (
              typeof argSchema.value !== 'string' &&
              !checkValueIsAddressIn({
                value: argValue,
                schema: argSchema.value,
                transaction,
                relatedAddresses,
              })
            ) {
              unmatched = true
              break
            }
          }
          if (unmatched) {
            continue
          }
        }

        targetLogs.push(log)
      }

      const log = targetLogs[schema.index]
      if (schema.value === 'address') {
        return log.address
      }
      const arg =
        log.args === undefined ? undefined : log.args[schema.value].value
      if (schema.paramKeys === undefined) {
        return stringify(arg).toLowerCase()
      }
      let value = arg
      for (const key of schema.paramKeys) {
        if (typeof key === 'number') {
          value = (value as unknown[])[key]
        }
        value = (value as Record<string, unknown>)[key]
      }
      return stringify(value).toLowerCase()
    }
    case 'erc20': {
      const targetTransfers: Erc20TokenTransfer[] = []
      for (const transfer of transaction.erc20Transfers) {
        // address
        if (
          schema.address !== undefined &&
          !checkValueIsAddressIn({
            value: transfer.address,
            schema: schema.address,
            transaction,
            relatedAddresses,
          })
        ) {
          continue
        }

        // from
        if (
          schema.from !== undefined &&
          !checkValueIsAddressIn({
            value: transfer.from,
            schema: schema.from,
            transaction,
            relatedAddresses,
          })
        ) {
          continue
        }

        // to
        if (
          schema.to !== undefined &&
          !checkValueIsAddressIn({
            value: transfer.to,
            schema: schema.to,
            transaction,
            relatedAddresses,
          })
        ) {
          continue
        }

        // amount
        if (
          schema.amount !== undefined &&
          !checkValueIsBetweenBigInt({
            value: transfer.value,
            schema: schema.amount,
          })
        ) {
          continue
        }

        targetTransfers.push(transfer)
      }

      const transfer = targetTransfers[schema.index]
      const value = schema.value

      switch (value) {
        case 'address': {
          return transfer.address
        }
        case 'from': {
          return transfer.from
        }
        case 'to': {
          return transfer.to
        }
        case 'amount': {
          return transfer.value.toString()
        }
        case 'amount-decimal': {
          return toStringBigDecimal(
            createBigDecimal(transfer.value, transfer.token.decimals),
          )
        }
        case 'currency-id': {
          return transfer.token.currency.id
        }
        case 'currency-name': {
          return transfer.token.currency.name
        }
        case 'currency-symbol': {
          return transfer.token.currency.symbol
        }
        default: {
          throw new Error(`Unknown ERC20 value: ${value satisfies never}`)
        }
      }
    }
    case 'erc721': {
      const targetTransfers: Erc721TokenTransfer[] = []
      for (const transfer of transaction.erc721Transfers) {
        // address
        if (
          schema.address !== undefined &&
          !checkValueIsAddressIn({
            value: transfer.address,
            schema: schema.address,
            transaction,
            relatedAddresses,
          })
        ) {
          continue
        }

        // from
        if (
          schema.from !== undefined &&
          !checkValueIsAddressIn({
            value: transfer.from,
            schema: schema.from,
            transaction,
            relatedAddresses,
          })
        ) {
          continue
        }

        // to
        if (
          schema.to !== undefined &&
          !checkValueIsAddressIn({
            value: transfer.to,
            schema: schema.to,
            transaction,
            relatedAddresses,
          })
        ) {
          continue
        }

        // id
        if (schema.id !== undefined && transfer.tokenId !== schema.id) {
          continue
        }

        targetTransfers.push(transfer)
      }

      const transfer = targetTransfers[schema.index]
      const value = schema.value

      switch (value) {
        case 'address': {
          return transfer.address
        }
        case 'from': {
          return transfer.from
        }
        case 'to': {
          return transfer.to
        }
        case 'id': {
          return transfer.tokenId.toString()
        }
        case 'nft-id': {
          return transfer.token.id
        }
        case 'nft-name': {
          return transfer.token.name ?? ''
        }
        default: {
          throw new Error(`Unknown ERC721 value: ${value satisfies never}`)
        }
      }
    }
    case 'erc1155': {
      const targetTransfers: Erc1155TokenTransfer[] = []
      for (const transfer of transaction.erc1155Transfers) {
        // address
        if (
          schema.address !== undefined &&
          !checkValueIsAddressIn({
            value: transfer.address,
            schema: schema.address,
            transaction,
            relatedAddresses,
          })
        ) {
          continue
        }

        // from
        if (
          schema.from !== undefined &&
          !checkValueIsAddressIn({
            value: transfer.from,
            schema: schema.from,
            transaction,
            relatedAddresses,
          })
        ) {
          continue
        }

        // to
        if (
          schema.to !== undefined &&
          !checkValueIsAddressIn({
            value: transfer.to,
            schema: schema.to,
            transaction,
            relatedAddresses,
          })
        ) {
          continue
        }

        // id
        if (schema.id !== undefined && transfer.tokenId !== schema.id) {
          continue
        }

        // amount
        if (
          schema.amount !== undefined &&
          !checkValueIsBetweenBigInt({
            value: transfer.value,
            schema: schema.amount,
          })
        ) {
          continue
        }

        targetTransfers.push(transfer)
      }

      const transfer = targetTransfers[schema.index]
      const value = schema.value

      switch (value) {
        case 'address': {
          return transfer.address
        }
        case 'from': {
          return transfer.from
        }
        case 'to': {
          return transfer.to
        }
        case 'id': {
          return transfer.tokenId.toString()
        }
        case 'amount': {
          return transfer.value.toString()
        }
        case 'nft-id': {
          return transfer.token.id
        }
        case 'nft-name': {
          return transfer.token.name ?? ''
        }
        default: {
          throw new Error(`Unknown ERC1155 value: ${value satisfies never}`)
        }
      }
    }
    default: {
      throw new Error(`Unknown simple value type: ${schema satisfies never}`)
    }
  }
}
